import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  axiosDelete,
  axiosGet,
  axiosPatch,
  axiosPost,
  axiosPut,
} from "../../utils/common/axios";
import { commerceModules } from "../../data/commerceModules";

const send = (method, url, body) =>
  ({
    GET: () => axiosGet(url),
    POST: () => axiosPost(url, body),
    PUT: () => axiosPut(url, body),
    PATCH: () => axiosPatch(url, body),
    DELETE: () => axiosDelete(url),
  })[method]();

const fill = (tpl, ctx) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => encodeURIComponent(ctx?.[k] ?? ""));
const usedKeys = (tpl) => [...tpl.matchAll(/\{(\w+)\}/g)].map((m) => m[1]);

const errorText = (e) => {
  const d = e?.data;
  if (e?.status === 403 && d?.code) return null; // axios interceptor already toasts it
  return d?.message || d?.code || (typeof d === "string" && d) || e?.message || "Request failed";
};
const fail = (e) => {
  const m = errorText(e);
  if (m) toast.error(m);
};
const isUnavailable = (e) =>
  e?.status === 404 || (e?.status === 403 && String(e?.data?.code).startsWith("ADMIN_ROLE"));

const toForm = (fields, row, ctx) =>
  Object.fromEntries(
    fields.map((f) => {
      let v = row?.[f.name];
      if (v == null && f.default) v = f.default === "$companyId" ? ctx.companyId : f.default;
      if (f.type === "json") v = v == null ? "" : JSON.stringify(v);
      else if (f.type === "list") v = Array.isArray(v) ? v.join(",") : v ?? "";
      else if (f.type === "checkbox") v = !!v;
      else if (f.type === "datetime-local" && typeof v === "string") v = v.slice(0, 16);
      else if (v == null) v = "";
      return [f.name, v];
    })
  );

const toBody = (fields, form) => {
  const body = {};
  fields.forEach((f) => {
    const v = form[f.name];
    if (f.type === "checkbox") body[f.name] = !!v;
    else if (v === "" || v == null) return;
    else if (f.type === "number") body[f.name] = Number(v);
    else if (f.type === "list")
      body[f.name] = String(v).split(",").map((x) => x.trim()).filter(Boolean).map((x) => (isNaN(x) ? x : Number(x)));
    else if (f.type === "json") body[f.name] = JSON.parse(v);
    else body[f.name] = v;
  });
  return body;
};

const Field = ({ f, value, onChange }) => {
  if (f.type === "checkbox")
    return (
      <FormControlLabel
        control={<Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} />}
        label={f.label}
      />
    );
  const select = f.type === "select";
  return (
    <TextField
      size="small"
      fullWidth
      select={select}
      multiline={f.type === "textarea" || f.type === "json"}
      label={f.label}
      type={["number", "date", "datetime-local", "password"].includes(f.type) ? f.type : "text"}
      InputLabelProps={["date", "datetime-local"].includes(f.type) ? { shrink: true } : undefined}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    >
      {select &&
        f.options.map((o) => (
          <MenuItem key={o} value={o}>
            {o || "(any)"}
          </MenuItem>
        ))}
    </TextField>
  );
};

const Fields = ({ fields, form, setForm }) => (
  <Stack spacing={2} sx={{ mt: 1 }}>
    {fields.map((f) => (
      <Field key={f.name} f={f} value={form[f.name]} onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))} />
    ))}
  </Stack>
);

const Result = ({ data }) =>
  data === undefined ? null : (
    <Box component="pre" sx={{ mt: 2, p: 2, bgcolor: "action.hover", overflow: "auto", fontSize: 13, maxHeight: 420 }}>
      {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
    </Box>
  );

const cell = (v) =>
  v == null ? "" : typeof v === "object" ? JSON.stringify(v).slice(0, 60) : String(v);

// Runs a request described by `def` (method/url/fields) with row context.
const RequestDialog = ({ def, row, ctx, onClose, onDone, setResult }) => {
  const fields = def.fields || [];
  const [form, setForm] = useState(() => toForm(fields, row, ctx));
  const run = async () => {
    try {
      const all = { ...row, ...form };
      const used = usedKeys(def.url);
      let body = toBody(fields.filter((f) => !used.includes(f.name)), form);
      if (def.bodyField) body = body[def.bodyField];
      const { data } = await send(def.method || "POST", fill(def.url, all), def.method === "GET" ? undefined : body);
      if (def.method === "GET") setResult?.(data);
      else toast.success("Done");
      onDone?.(data);
      onClose();
    } catch (e) {
      if (e instanceof SyntaxError) toast.error("Invalid JSON");
      else fail(e);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{def.label || def.title}</DialogTitle>
      <DialogContent>
        {fields.length ? <Fields fields={fields} form={form} setForm={setForm} /> : <Typography>Confirm this action?</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={run}>
          {def.method === "GET" ? "Fetch" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ParamsBar = ({ params, values, setValues, onApply }) =>
  params?.length ? (
    <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap", rowGap: 2 }}>
      {params.map((p) => (
        <Box key={p.name} sx={{ minWidth: 150 }}>
          <Field f={p} value={values[p.name]} onChange={(v) => setValues((s) => ({ ...s, [p.name]: v }))} />
        </Box>
      ))}
      <Button variant="outlined" onClick={onApply}>
        Apply
      </Button>
    </Stack>
  ) : null;

const query = (params, values) => {
  const q = new URLSearchParams();
  (params || []).forEach((p) => values[p.name] !== "" && values[p.name] != null && q.set(p.name, values[p.name]));
  return q.toString();
};

const useLoader = (res) => {
  const [values, setValues] = useState({});
  const [data, setData] = useState();
  const [off, setOff] = useState(false);
  const load = useCallback(
    async (vals = values) => {
      try {
        const q = query(res.params, vals);
        const { data: d } = await send("GET", res.url + (q ? `?${q}` : ""));
        setData(d);
        setOff(false);
      } catch (e) {
        if (isUnavailable(e)) setOff(true);
        else fail(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [res.url]
  );
  useEffect(() => {
    setValues({});
    setData(undefined);
    load({});
  }, [load]);
  return { values, setValues, data, off, load };
};

const TableResource = ({ res, ctx }) => {
  const { values, setValues, data, off, load } = useLoader(res);
  const [dialog, setDialog] = useState(null);
  const [result, setResult] = useState();
  const rows = res.pick && data ? data[res.pick] : data;
  if (off) return <Alert severity="info">This feature is not available (turned off or no permission).</Alert>;
  const list = Array.isArray(rows) ? rows : null;
  const cols = list?.length
    ? Object.keys(list[0]).filter((k) => list[0][k] == null || typeof list[0][k] !== "object").slice(0, 8)
    : [];
  const open = (def, row) => setDialog({ def, row });
  const remove = async (row) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await send("DELETE", fill(res.del.url, row));
      toast.success("Deleted");
      load();
    } catch (e) {
      fail(e);
    }
  };
  return (
    <>
      <ParamsBar params={res.params} values={values} setValues={setValues} onApply={() => load(values)} />
      {res.create && (
        <Button variant="contained" sx={{ mb: 2 }} onClick={() => open({ label: "Create", method: res.create.method || "POST", url: res.create.url, fields: res.create.fields })}>
          Add new
        </Button>
      )}
      {list ? (
        <Box sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {cols.map((c) => (
                  <TableCell key={c}>{c}</TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row, i) => (
                <TableRow key={row.id ?? i}>
                  {cols.map((c) => (
                    <TableCell key={c}>{cell(row[c])}</TableCell>
                  ))}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {res.edit && (
                      <Button size="small" onClick={() => open({ label: "Edit", method: res.edit.method, url: res.edit.url, fields: res.edit.fields }, row)}>
                        Edit
                      </Button>
                    )}
                    {(res.actions || []).map((a) => (
                      <Button key={a.label} size="small" onClick={() => open(a, row)}>
                        {a.label}
                      </Button>
                    ))}
                    {res.del && (
                      <Button size="small" color="error" onClick={() => remove(row)}>
                        Delete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!list.length && (
                <TableRow>
                  <TableCell colSpan={cols.length + 1}>No records</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Result data={data} />
      )}
      <Result data={result} />
      {dialog && (
        <RequestDialog
          key={JSON.stringify(dialog.row?.id ?? dialog.def.label)}
          def={dialog.def}
          row={dialog.row}
          ctx={ctx}
          setResult={setResult}
          onClose={() => setDialog(null)}
          onDone={() => dialog.def.method !== "GET" && load()}
        />
      )}
    </>
  );
};

const JsonResource = ({ res }) => {
  const { values, setValues, data, off, load } = useLoader(res);
  const [dialog, setDialog] = useState(false);
  if (off) return <Alert severity="info">This feature is not available (turned off or no permission).</Alert>;
  return (
    <>
      <ParamsBar params={res.params} values={values} setValues={setValues} onApply={() => load(values)} />
      {res.run && (
        <Button variant="outlined" sx={{ mb: 2 }} onClick={() => setDialog(true)}>
          {res.run.label}
        </Button>
      )}
      <Result data={data} />
      {dialog && <RequestDialog def={res.run} onClose={() => setDialog(false)} onDone={() => load()} />}
    </>
  );
};

const FormResource = ({ res, ctx }) => {
  const { data, off, load } = useLoader(res);
  const [form, setForm] = useState({});
  useEffect(() => {
    if (data && typeof data === "object") setForm(toForm(res.fields, data, ctx));
  }, [data, res.fields, ctx]);
  if (off) return <Alert severity="info">This feature is not available (turned off or no permission).</Alert>;
  const save = async () => {
    try {
      await send(res.save.method, res.save.url, { ...(data || {}), ...toBody(res.fields, form) });
      toast.success("Saved");
      load();
    } catch (e) {
      fail(e);
    }
  };
  return (
    <Box sx={{ maxWidth: 520 }}>
      <Fields fields={res.fields} form={form} setForm={setForm} />
      <Button variant="contained" sx={{ mt: 2 }} onClick={save}>
        Save
      </Button>
    </Box>
  );
};

const ActionResource = ({ res, ctx }) => {
  const [form, setForm] = useState(() => toForm(res.fields, null, ctx));
  const [result, setResult] = useState();
  const run = async () => {
    try {
      const used = usedKeys(res.url);
      const body = toBody(res.fields.filter((f) => !used.includes(f.name)), form);
      const { data } = await send(res.method, fill(res.url, form), res.method === "GET" ? undefined : body);
      setResult(data === "" || data == null ? "Done" : data);
    } catch (e) {
      if (e instanceof SyntaxError) toast.error("Invalid JSON");
      else fail(e);
    }
  };
  return (
    <Box sx={{ maxWidth: 520 }}>
      <Fields fields={res.fields} form={form} setForm={setForm} />
      <Button variant="contained" sx={{ mt: 2 }} onClick={run}>
        {res.method === "GET" ? "Fetch" : "Run"}
      </Button>
      <Result data={result} />
    </Box>
  );
};

const views = { table: TableResource, json: JsonResource, form: FormResource, action: ActionResource };

const CommercePage = () => {
  const { module } = useParams();
  const [tab, setTab] = useState(0);
  const user = useSelector((s) => s?.login?.user);
  const mod = commerceModules[module];
  const ctx = useMemo(() => ({ companyId: user?.companyId }), [user?.companyId]);
  useEffect(() => setTab(0), [module]);
  if (!mod) return <Typography sx={{ p: 3 }}>Unknown section</Typography>;
  const res = mod.resources[tab] || mod.resources[0];
  const View = views[res.type];
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {mod.title}
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 3 }}>
        {mod.resources.map((r) => (
          <Tab key={r.title} label={r.title} />
        ))}
      </Tabs>
      <View key={`${module}-${tab}`} res={res} ctx={ctx} />
    </Box>
  );
};

export default CommercePage;
